/**
 * Image parser — extracts images from DOCX and converts to ProseMirror image nodes.
 *
 * OOXML image structures:
 *
 * Modern (DrawingML):
 *   <w:drawing>
 *     <wp:inline> or <wp:anchor>
 *       <wp:extent cx="..." cy="..." />  ← size in EMUs
 *       <a:graphic>
 *         <a:graphicData>
 *           <pic:pic>
 *             <pic:blipFill>
 *               <a:blip r:embed="rId..." />  ← relationship ID to media file
 *
 * Legacy (VML):
 *   <w:pict>
 *     <v:shape style="width:...;height:...">
 *       <v:imagedata r:id="rId..." />
 *
 * ProseMirror image node:
 *   { type: "image", attrs: { src, alt, width, height, alignment } }
 */

import JSZip from 'jszip';
import { PMNode, ParseContext } from './types';
import { emuToPixels } from './zipReader';

const WP_NS =
  'http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing';
const A_NS = 'http://schemas.openxmlformats.org/drawingml/2006/main';
const PIC_NS =
  'http://schemas.openxmlformats.org/drawingml/2006/picture';
const R_NS =
  'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const V_NS = 'urn:schemas-microsoft-com:vml';

/**
 * Parse a <w:drawing> element into an image node.
 * Returns null if the drawing doesn't contain a recognizable image.
 */
export async function parseDrawing(
  drawingEl: Element,
  ctx: ParseContext,
  zip: JSZip
): Promise<PMNode | null> {
  // Look for <wp:inline> or <wp:anchor>
  const inline = getElementByLocalName(drawingEl, 'inline');
  const anchor = getElementByLocalName(drawingEl, 'anchor');
  const container = inline || anchor;

  if (!container) return null;

  // Extract dimensions from <wp:extent>
  const extentEl = getElementByLocalName(container, 'extent');
  let width: number | undefined;
  let height: number | undefined;
  if (extentEl) {
    const cx = extentEl.getAttribute('cx');
    const cy = extentEl.getAttribute('cy');
    if (cx) width = emuToPixels(parseInt(cx, 10));
    if (cy) height = emuToPixels(parseInt(cy, 10));
  }

  // Navigate to <a:blip> for the relationship ID
  const blip = findDescendantByLocalName(container, 'blip');
  if (!blip) return null;

  const rId =
    blip.getAttributeNS(R_NS, 'embed') || blip.getAttribute('r:embed') || '';
  if (!rId) return null;

  // Resolve relationship to media file path
  const rel = ctx.relationships.get(rId);
  if (!rel) return null;

  // Extract image data
  const src = await extractImageAsDataUrl(rel.target, zip);
  if (!src) return null;

  // Alt text from <wp:docPr>
  const docPr = getElementByLocalName(container, 'docPr');
  const alt =
    docPr?.getAttribute('descr') || docPr?.getAttribute('name') || '';

  const attrs: Record<string, unknown> = { src };
  if (alt) attrs.alt = alt;
  if (width) attrs.width = width;
  if (height) {
    // Cap at 800px to match ResizableImage max
    attrs.height = Math.min(height, 800);
  }
  attrs.alignment = 'center'; // default alignment

  return { type: 'image', attrs };
}

/**
 * Parse a <w:pict> (VML) element into an image node.
 * Returns null if not a recognizable image.
 */
export async function parsePict(
  pictEl: Element,
  ctx: ParseContext,
  zip: JSZip
): Promise<PMNode | null> {
  // Look for <v:shape> containing <v:imagedata>
  const imagedata = findDescendantByLocalName(pictEl, 'imagedata');
  if (!imagedata) return null;

  const rId =
    imagedata.getAttributeNS(R_NS, 'id') ||
    imagedata.getAttribute('r:id') ||
    '';
  if (!rId) return null;

  const rel = ctx.relationships.get(rId);
  if (!rel) return null;

  const src = await extractImageAsDataUrl(rel.target, zip);
  if (!src) return null;

  // Try to get dimensions from parent v:shape style
  let width: number | undefined;
  let height: number | undefined;
  const shape = findDescendantByLocalName(pictEl, 'shape');
  if (shape) {
    const style = shape.getAttribute('style') || '';
    const wMatch = style.match(/width:\s*([\d.]+)(?:pt|px)/);
    const hMatch = style.match(/height:\s*([\d.]+)(?:pt|px)/);
    if (wMatch) width = Math.round(parseFloat(wMatch[1]));
    if (hMatch) height = Math.min(Math.round(parseFloat(hMatch[1])), 800);
  }

  const attrs: Record<string, unknown> = { src };
  if (width) attrs.width = width;
  if (height) attrs.height = height;
  attrs.alignment = 'center';

  return { type: 'image', attrs };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract an image from the DOCX ZIP as a data URL (base64).
 */
async function extractImageAsDataUrl(
  target: string,
  zip: JSZip
): Promise<string | null> {
  // Target path is relative to word/, e.g. "media/image1.png"
  const path = target.startsWith('word/') ? target : `word/${target}`;
  const entry = zip.file(path);
  if (!entry) return null;

  const data = await entry.async('base64');
  const mimeType = guessMimeType(target);
  return `data:${mimeType};base64,${data}`;
}

/**
 * Guess MIME type from file extension.
 */
function guessMimeType(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const mimeMap: Record<string, string> = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    bmp: 'image/bmp',
    tiff: 'image/tiff',
    tif: 'image/tiff',
    svg: 'image/svg+xml',
    wmf: 'image/x-wmf',
    emf: 'image/x-emf',
  };
  return mimeMap[ext] || 'image/png';
}

/**
 * Find the first child element matching a local name (ignoring namespace).
 */
function getElementByLocalName(
  parent: Element,
  localName: string
): Element | null {
  const children = parent.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType === 1) {
      const el = child as Element;
      const elLocal = el.localName || el.nodeName.split(':').pop() || '';
      if (elLocal === localName) return el;
    }
  }
  return null;
}

/**
 * Find a descendant element by local name (depth-first search).
 */
function findDescendantByLocalName(
  parent: Element,
  localName: string
): Element | null {
  const children = parent.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType === 1) {
      const el = child as Element;
      const elLocal = el.localName || el.nodeName.split(':').pop() || '';
      if (elLocal === localName) return el;
      const found = findDescendantByLocalName(el, localName);
      if (found) return found;
    }
  }
  return null;
}
